import type { Document, DocumentVersion } from "@models";
import type { JSONContent } from "@tiptap/core";
import { getDocVersionRevisionNumber } from "src/utils";

export type ExportMetadataContext = {
  document: Document;
  exportedAt?: Date;
  organizationName: string;
  selectedDocVersion: DocumentVersion;
};

type ExportMetadataTableRow = Array<string | number>;

const textNode = (
  text: string | number,
  marks?: JSONContent["marks"]
): JSONContent => ({
  type: "text",
  text: String(text),
  ...(marks ? { marks } : {}),
});

const paragraphNode = (
  text?: string | number,
  marks?: JSONContent["marks"]
): JSONContent => ({
  type: "paragraph",
  content: text === undefined || text === "" ? [] : [textNode(text, marks)],
});

const headingNode = (text: string): JSONContent => ({
  type: "heading",
  attrs: {
    level: 2,
  },
  content: [textNode(text)],
});

const pageBreakNode = (): JSONContent => ({
  type: "pageBreak",
});

const summaryHeadingNode = (text: string): JSONContent => ({
  type: "heading",
  attrs: {
    level: 1,
  },
  content: [textNode(text)],
});

const tableCellNode = (
  value: string | number,
  marks?: JSONContent["marks"]
): JSONContent => ({
  type: "tableCell",
  attrs: {
    colspan: 1,
    rowspan: 1,
    colwidth: null,
  },
  content: [paragraphNode(value, marks)],
});

const tableRowNode = (
  row: ExportMetadataTableRow,
  marks?: JSONContent["marks"]
): JSONContent => ({
  type: "tableRow",
  content: row.map((cell) => tableCellNode(cell, marks)),
});

const tableNode = (
  headers: ExportMetadataTableRow,
  rows: ExportMetadataTableRow[]
) => ({
  type: "table",
  content: [
    tableRowNode(headers, [{ type: "bold" }]),
    ...rows.map((row) => tableRowNode(row)),
  ],
});

const escapeMarkdownTableCell = (value: string | number) => {
  const text = String(value).replace(/\r?\n/g, " ").replace(/\|/g, "\\|");

  return text.trim() || " ";
};

const markdownTable = (
  headers: ExportMetadataTableRow,
  rows: ExportMetadataTableRow[]
) => [
  `| ${headers.map(escapeMarkdownTableCell).join(" | ")} |`,
  `| ${headers.map(() => "---").join(" | ")} |`,
  ...rows.map((row) => `| ${row.map(escapeMarkdownTableCell).join(" | ")} |`),
];

const formatExportDateTime = (date: Date) => {
  const iso = date.toISOString();

  return `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;
};

const getRevisionStatus = (
  selectedDocVersion: DocumentVersion,
  documentVersions: DocumentVersion[]
) => {
  if (selectedDocVersion.revision === null) {
    return selectedDocVersion.readyForApproval ? "Ready for approval" : "Draft";
  }

  const revisions = [selectedDocVersion, ...documentVersions]
    .map((version) => version.revision)
    .filter((revision): revision is number => typeof revision === "number");
  const latestRevision = Math.max(...revisions);

  return selectedDocVersion.revision === latestRevision ? "Live" : "Archived";
};

const getExportMetadataTables = ({
  document,
  exportedAt = new Date(),
  organizationName,
  selectedDocVersion,
}: ExportMetadataContext) => {
  const signatureTable = selectedDocVersion.signatureTable;
  if (!signatureTable) {
    throw new Error("Document signature data is not available for export.");
  }

  const revision = getDocVersionRevisionNumber(
    selectedDocVersion,
    document.versions
  );
  const status = getRevisionStatus(selectedDocVersion, document.versions);
  const summaryRows = [
    ["Organization", organizationName.trim()],
    ["Document", document.title],
    ["Document Number", document.id],
    ["Exported Revision", `Revision ${revision} ${status}`],
    ["Exported At", formatExportDateTime(exportedAt)],
  ];
  const signatureRows = signatureTable.signatureRows.map((row) =>
    signatureTable.signatureColumns.map((column) => row[column.key])
  );
  const revisionRows = signatureTable.revisionHistoryRows.map((row) =>
    signatureTable.revisionHistoryColumns.map((column) => row[column.key])
  );
  return {
    signatureHeaders: signatureTable.signatureColumns.map(
      (column) => column.label
    ),
    revisionRows,
    revisionHeaders: signatureTable.revisionHistoryColumns.map(
      (column) => column.label
    ),
    signatureRows,
    summaryRows,
  };
};

export const buildExportMetadataNodes = (
  context: ExportMetadataContext
): JSONContent[] => {
  const tables = getExportMetadataTables(context);

  return [
    summaryHeadingNode("Document Export Summary"),
    tableNode(["Field", "Value"], tables.summaryRows),
    headingNode("Document Signature Table"),
    tableNode(tables.signatureHeaders, tables.signatureRows),
    headingNode("Revision History"),
    tableNode(tables.revisionHeaders, tables.revisionRows),
    pageBreakNode(),
  ];
};

export const addExportMetadata = (
  document: JSONContent,
  context: ExportMetadataContext
): JSONContent => {
  const metadataNodes = buildExportMetadataNodes(context);

  return {
    ...document,
    content: [...metadataNodes, ...(document.content ?? [])],
  };
};

export const buildExportMetadataMarkdown = (context: ExportMetadataContext) => {
  const tables = getExportMetadataTables(context);

  return [
    "# Document Export Summary",
    ...markdownTable(["Field", "Value"], tables.summaryRows),
    "",
    "## Document Signature Table",
    ...markdownTable(tables.signatureHeaders, tables.signatureRows),
    "",
    "## Revision History",
    ...markdownTable(tables.revisionHeaders, tables.revisionRows),
    "",
    '<div class="pdf-page-break"></div>',
  ].join("\n");
};
